import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { useCallback, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
    WORKSPACE_MANUFACTURER_SELECTED_KEY,
    WorkspaceManufacturerSelected,
} from 'shared/utils/workspaceUtils'
import { useElementItemStore } from '../../../ElementItemStore'
import {
    useSelectedProjectElementNode,
    useShallowSelector,
} from '../../../Workspace2Store'
import { useProductFetcher } from '../../../api/useProductFetcher/useProductFetcher'
import { useTrackChangesQuery } from '../../../components/TrackChangesPanel/api'
import { ContentItemViewNodesType } from '../../../types'
import { findRootArticle } from '../../../utils/contentItemViewNodesUtils'
import { HyperlinkParentNode } from '../types'
import { extractElementIds } from './utils/elementIdExtractor'

/**
 * Updates node selection state for hyperlink elements
 */
const updateNodeSelectionState = (
    nodes: ContentItemViewNodesType[],
    listingId: string,
    isSelected: boolean
): {
    updatedNodes: ContentItemViewNodesType[]
    hasChanges: boolean
    affectedNode: (HyperlinkParentNode & { id: string }) | null
} => {
    let hasChanges = false
    let affectedNode: (HyperlinkParentNode & { id: string }) | null = null

    const updatedNodes = nodes.map((node) => {
        // Check if this node has a hyperlink element with matching pillId or id
        const hasMatchingHyperlink = node.elements?.some((el) => {
            const nodeWithPill = node as HyperlinkParentNode
            return (
                el.type === 'Hyperlink' &&
                (nodeWithPill.pillId === listingId || el.id === listingId)
            )
        })

        if (hasMatchingHyperlink) {
            const nodeWithPill = node as ContentItemViewNodesType &
                HyperlinkParentNode

            // Check if the selection state is actually changing
            if (nodeWithPill.isPillSelected !== isSelected) {
                hasChanges = true
                if (node.id) {
                    affectedNode = {
                        ...nodeWithPill,
                        id: node.id,
                    } as HyperlinkParentNode & { id: string }
                }

                return {
                    ...node,
                    isPillSelected: isSelected,
                }
            }
        }

        return node
    })

    return { updatedNodes, hasChanges, affectedNode }
}

/**
 * Hook to listen for service worker messages and update hyperlink selection states
 */
export const useServiceWorkerListener = (
    elementId?: string,
    selectedProjectElementNodeFromProps?: Partial<ProjectElementNode> | null
) => {
    const { projectId: projectIdFromParams } = useParams<{
        projectId?: string
    }>()
    const {
        contentItemViewNodes,
        setContentItemViewNodes,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
    }))

    // Get element item store for clearing element value
    const elementItemValue = useElementItemStore((s) => s.elementItemValue)
    const setElementItemValue = useElementItemStore(
        (s) => s.setElementItemValue
    )

    // Use the prop if provided, otherwise fall back to the store
    const selectedProjectElementNodeFromStore = useSelectedProjectElementNode()

    // Check if prop is provided and not null/undefined, otherwise use store
    // Also check if store value is not an empty object
    const isStoreNodeEmpty =
        !selectedProjectElementNodeFromStore ||
        (typeof selectedProjectElementNodeFromStore === 'object' &&
            Object.keys(selectedProjectElementNodeFromStore).length === 0)

    const selectedProjectElementNode =
        selectedProjectElementNodeFromProps ||
        (!isStoreNodeEmpty ? selectedProjectElementNodeFromStore : null)

    // Get content view update functions from useProductFetcher
    // Use overrideContentItemNodes: false to merge content instead of replacing
    const { getContentItemViewNodesAPI } = useProductFetcher({
        projectId: projectIdFromParams || '',
        overrideContentItemNodes: false,
    })

    // Get track changes refresh function
    const { fetchTrackChangesData } = useTrackChangesQuery()

    // Simple race condition protection
    const operationInProgress = useRef(false)

    // Memoized message handler to avoid recreating on every render
    const handleServiceWorkerMessage = useCallback(
        async ({ data }: MessageEvent) => {
            // Validate message data structure
            if (!data || typeof data !== 'object') {
                return
            }

            // Simple protection against concurrent operations
            if (operationInProgress.current) {
                return
            }

            operationInProgress.current = true

            try {
                // Check for manufacturer selection state changes
                const workspaceManufacturerSelectedState = data?.[
                    WORKSPACE_MANUFACTURER_SELECTED_KEY
                ] as WorkspaceManufacturerSelected

                // Extract elementId and nodeId using utility function
                const {
                    elementId: elementIdToUse,
                    nodeId: nodeIdFromMessage,
                } = extractElementIds(data)

                if (
                    workspaceManufacturerSelectedState !== undefined &&
                    workspaceManufacturerSelectedState !== null
                ) {
                    const {
                        listingId,
                        isSelected,
                    } = workspaceManufacturerSelectedState

                    // Update contentItemViewNodes to reflect the selection state
                    const { updatedNodes, hasChanges, affectedNode } = updateNodeSelectionState(
                        contentItemViewNodes as ContentItemViewNodesType[],
                        listingId,
                        isSelected
                    )

                    // Update nodes if there were changes
                    if (hasChanges) {
                        setContentItemViewNodes(updatedNodes)
                    }

                    // Always refresh the content view and track changes after a manufacturer selection change
                    try {
                        // Ensure functions are available
                        if (
                            !getContentItemViewNodesAPI ||
                            !fetchTrackChangesData
                        ) {
                            return
                        }

                        // Use the nodeId from the message
                        let finalNodeId = nodeIdFromMessage

                        // Update element item value
                        if (
                            (elementIdToUse &&
                                elementIdToUse !==
                                    elementItemValue.elementId) ||
                            (finalNodeId && finalNodeId !== elementItemValue.id)
                        ) {
                            setElementItemValue({
                                ...elementItemValue,
                                elementId:
                                    elementIdToUse ||
                                    elementItemValue.elementId,
                                id: finalNodeId || elementItemValue.id,
                            })
                        }

                        // Refresh content and track changes
                        if (elementIdToUse && finalNodeId) {
                            // Find articles for content refresh
                            const currentNodes = (hasChanges
                                ? updatedNodes
                                : contentItemViewNodes) as ContentItemViewNodesType[]
                            let articleIds: string[] = []

                            if (affectedNode) {
                                const rootId = findRootArticle(
                                    (affectedNode as HyperlinkParentNode & {
                                        id: string
                                    }).id,
                                    currentNodes
                                )
                                if (rootId) articleIds = [rootId]
                            }

                            if (articleIds.length === 0) {
                                const articles = currentNodes.filter(
                                    (n) =>
                                        n.textType === 'Article' ||
                                        n.type === 'Article'
                                )
                                articleIds = articles
                                    .map((n) => n.id)
                                    .filter(Boolean) as string[]
                            }

                            try {
                                await getContentItemViewNodesAPI(
                                    articleIds.length > 0 ? articleIds : null,
                                    undefined,
                                    elementIdToUse,
                                    finalNodeId
                                )
                                await fetchTrackChangesData(elementIdToUse)
                            } catch (error) {
                                // Content refresh errors are not critical
                            }
                        }
                    } catch (error) {
                        // Don't throw - this is not critical to the selection operation
                    }
                }
            } catch (error) {
                // Don't throw - this is not critical to the selection operation
            } finally {
                // Always clear the operation flag, even if there was an error
                operationInProgress.current = false
            }
        },
        [
            contentItemViewNodes,
            selectedProjectElementNode,
            elementItemValue,
            projectIdFromParams,
            setElementItemValue,
            getContentItemViewNodesAPI,
            fetchTrackChangesData,
            setContentItemViewNodes,
        ]
    )

    useEffect(() => {
        const serviceWorker = navigator.serviceWorker

        if (!serviceWorker) {
            return
        }

        serviceWorker.addEventListener('message', handleServiceWorkerMessage)

        return () => {
            serviceWorker.removeEventListener(
                'message',
                handleServiceWorkerMessage
            )
        }
    }, [handleServiceWorkerMessage])
}
